// src/utils/rattachementPhotos.ts — SCE Issues
// À qui appartient une photo : au problème (déclaration) ou à un événement
// de l'activité.
//
// Avant le 09/10, rien ne gardait ce lien : la fiche le DEVINAIT, une photo
// envoyée à moins de 30 s d'un événement lui revenait. À la déclaration,
// l'événement « Problème déclaré » est écrit juste avant l'envoi des photos :
// en Wi-Fi, elles arrivaient dans les 30 s et partaient dans l'activité ; en
// 4G faible, non, et elles restaient sous la description. D'où des photos
// « cachées dans l'activité » selon les utilisateurs (P2026-0044).
//
// Désormais :
//  1. une photo envoyée avec un commentaire porte son identifiant dans son
//     nom de fichier (`P2026-0044-C03__photo.jpg`) : le lien est écrit, plus
//     deviné. Pas de colonne SharePoint à créer ;
//  2. les photos plus anciennes, sans marque, gardent la règle des 30 s, mais
//     jamais avec « Problème déclaré » : ce sont les photos du problème.

import type { PieceJointe } from '@/api/files.api'
import type { Commentaire } from '@/types/issue.types'

/** Delta max entre l'envoi d'une photo SANS MARQUE et un événement (ancien rattachement). */
export const SEUIL_RATTACHEMENT_MS = 30_000

/** `P2026-0044-C03__` n'importe où dans le nom (l'envoi simple le préfixe d'un horodatage). */
const MARQUE = /(P\d{4}-\d+-C\d+)__/

/** Identifiant d'événement utilisable comme marque (pas le repli `#12` d'un item sans Title). */
function marquable(commentaireId: string): boolean {
  return MARQUE.test(`${commentaireId}__`)
}

/** Nom du fichier envoyé avec un commentaire : la marque, puis le nom d'origine. */
export function nommerPourCommentaire(nom: string, commentaireId: string): string {
  if (!marquable(commentaireId) || MARQUE.test(nom)) return nom
  return `${commentaireId}__${nom}`
}

/** L'événement inscrit dans le nom du fichier, s'il y en a un. */
export function commentaireDuNom(nom: string): string | null {
  return MARQUE.exec(nom)?.[1] ?? null
}

/** Le nom à montrer : sans horodatage d'envoi ni marques. */
export function nomAffiche(nom: string): string {
  return nom
    .replace(/^\d{8}-\d{6}-/, '')
    .replace(/^.*?P\d{4}-\d+-C\d+__/, '')
    .replace(/^annote-[A-Za-z0-9!-]+__/, '')
}

// ─── Photos annotées ───
//
// L'original n'est jamais écrasé : la version annotée est un autre fichier,
// qui nomme son original (`annote-<id de l'original>__photo.jpg`). Le bloc
// Photos montre la version annotée à la place de l'original ; la visionneuse
// permet de revenir à l'original.

const ANNOTATION = /annote-([A-Za-z0-9!-]+)__/

/** Nom de la version annotée : un JPEG pour une photo, un PDF pour un PDF (12/10). */
export function nommerAnnotation(nomOriginal: string, idOriginal: string, extension: 'jpg' | 'pdf' = 'jpg'): string {
  const base = nomAffiche(nomOriginal).replace(/\.[^.]+$/, '') || (extension === 'pdf' ? 'document' : 'photo')
  return `annote-${idOriginal}__${base}.${extension}`
}

/** L'original dont ce fichier est la version annotée, s'il en est une. */
export function originalAnnote(nom: string): string | null {
  return ANNOTATION.exec(nom)?.[1] ?? null
}

export interface RattachementPhotos {
  /** Photos du problème : envoyées à la déclaration, ou sans événement. */
  probleme: PieceJointe[]
  /** Identifiant d'événement → ses photos. */
  parCommentaire: Map<string, PieceJointe[]>
}

/**
 * Range chaque pièce jointe : marque d'abord ; sinon l'événement le plus
 * proche dans le temps (< 30 s), « Problème déclaré » exclu ; sinon le
 * problème. Une marque vers un événement absent de la liste (pas encore
 * relu) reste dans l'activité : elle n'est pas une photo du problème.
 */
export function rattacherPhotos(
  piecesJointes: readonly PieceJointe[],
  commentaires: readonly Commentaire[],
): RattachementPhotos {
  const parCommentaire = new Map<string, PieceJointe[]>()
  const probleme: PieceJointe[] = []
  const ajouter = (id: string, pj: PieceJointe): void => {
    parCommentaire.set(id, [...(parCommentaire.get(id) ?? []), pj])
  }

  for (const pj of piecesJointes) {
    const marque = commentaireDuNom(pj.nom)
    if (marque) {
      ajouter(marque, pj)
      continue
    }

    const tPJ = new Date(pj.dateUpload).getTime()
    let meilleur: Commentaire | null = null
    let meilleurDelta = Infinity
    if (!Number.isNaN(tPJ)) {
      for (const c of commentaires) {
        if (c.type === 'Creation') continue
        const tC = new Date(c.date).getTime()
        if (Number.isNaN(tC)) continue
        const delta = Math.abs(tPJ - tC)
        if (delta < SEUIL_RATTACHEMENT_MS && delta < meilleurDelta) {
          meilleurDelta = delta
          meilleur = c
        }
      }
    }
    if (meilleur) ajouter(meilleur.id, pj)
    else probleme.push(pj)
  }

  return { probleme, parCommentaire }
}

// ─── Toutes les photos de la fiche, dans l'ordre du bloc Photos ───

/** Une photo du bloc Photos et de la visionneuse, avec de quoi la légender. */
export interface PhotoFiche {
  pj: PieceJointe
  origine: 'probleme' | 'activite'
  /** Qui l'a envoyée : le déclarant, ou l'auteur de l'événement. */
  auteur: string
  /** Son adresse, pour sa photo de profil (absente : les initiales seules). */
  auteurUpn?: string
  date: string
  /** L'événement d'une photo de l'activité (absent s'il n'est pas encore relu). */
  commentaire?: Commentaire
  /** Dernière version annotée : montrée par défaut. */
  annotation?: {
    pj: PieceJointe
    auteur: string
    date: string
    commentaire?: Commentaire
  }
}

/**
 * Le bloc Photos (09/10) : d'abord les photos du problème (déclaration, et
 * photos du groupe), puis celles de l'activité, dans l'ordre des événements.
 */
export function composerPhotosFiche(
  rattachement: RattachementPhotos,
  commentaires: readonly Commentaire[],
  declaration: { auteur: string; date: string; upn?: string },
  photosGroupe: readonly PieceJointe[] = [],
): PhotoFiche[] {
  const probleme: PhotoFiche[] = [...rattachement.probleme, ...photosGroupe].map((pj) => ({
    pj,
    origine: 'probleme',
    auteur: declaration.auteur,
    ...(declaration.upn ? { auteurUpn: declaration.upn } : {}),
    date: declaration.date || pj.dateUpload,
  }))

  const activite: PhotoFiche[] = []
  const vus = new Set<string>()
  for (const c of commentaires) {
    for (const pj of rattachement.parCommentaire.get(c.id) ?? []) {
      activite.push({ pj, origine: 'activite', auteur: c.auteur, ...(c.auteurEmail ? { auteurUpn: c.auteurEmail } : {}), date: c.date, commentaire: c })
    }
    vus.add(c.id)
  }
  // Événement pas encore relu : ses photos ferment la marche.
  for (const [id, pjs] of rattachement.parCommentaire) {
    if (vus.has(id)) continue
    for (const pj of pjs) activite.push({ pj, origine: 'activite', auteur: '', date: pj.dateUpload })
  }

  // Versions annotées : rangées sur leur original (la plus récente gagne),
  // sauf si l'original n'est plus là.
  const toutes = [...probleme, ...activite]
  const parId = new Map(toutes.map((p) => [p.pj.id, p]))
  const annotations = toutes
    .filter((p) => {
      const id = originalAnnote(p.pj.nom)
      return id !== null && id !== p.pj.id && parId.has(id)
    })
    .sort((a, b) => a.pj.dateUpload.localeCompare(b.pj.dateUpload))
  for (const a of annotations) {
    const original = parId.get(originalAnnote(a.pj.nom) ?? '')
    if (!original) continue
    original.annotation = { pj: a.pj, auteur: a.auteur, date: a.date, ...(a.commentaire ? { commentaire: a.commentaire } : {}) }
  }
  const rangees = new Set(annotations)
  return toutes.filter((p) => !rangees.has(p))
}

/** Texte écrit par l'application pour des fichiers envoyés sans message. */
export function estTexteAutomatique(contenu: string): boolean {
  return /^(Fichier ajouté|\d+ fichiers ajoutés|Photo annotée|PDF annoté)$/.test(contenu.trim())
}

/**
 * Ce que dit un commentaire écrit par l'appli (27/10) : des fichiers ajoutés
 * (combien), ou une photo / un PDF annoté ; null pour un vrai commentaire.
 * L'activité lui donne son icône et son verbe (« a ajouté 3 fichiers »).
 */
export function natureAutomatique(contenu: string): { genre: 'fichiers'; nombre: number } | { genre: 'annotation'; quoi: 'photo' | 'pdf' } | null {
  const t = contenu.trim()
  if (t === 'Fichier ajouté') return { genre: 'fichiers', nombre: 1 }
  const n = /^(\d+) fichiers ajoutés$/.exec(t)
  if (n) return { genre: 'fichiers', nombre: Number(n[1]) }
  if (t === 'Photo annotée') return { genre: 'annotation', quoi: 'photo' }
  if (t === 'PDF annoté') return { genre: 'annotation', quoi: 'pdf' }
  return null
}
